import axios from 'axios';
import { RuntimeConfig } from '~/models/runtime-config';
import { urlWithBasePath } from '~/utils/url-helpers';

/**
 * This function retrieves the runtime configuration from the server and caches it in browser session storage.
 * If the configuration is already cached, it returns the cached version.
 * If the configuration cannot be retrieved, it rejects the promise with an error.
 * @Note This function should only be called in a browser environment where sessionStorage is available.
 * @returns A promise that resolves to type RuntimeConfig.
 */
const getRuntimeConfig = async () => {
    return new Promise<RuntimeConfig>((resolve, reject) => {
        if (typeof window === 'undefined' || !window.sessionStorage) {
            reject(new Error('Session storage is not available in this environment.'));
            return;
        }

        const sessionStorageKey = 'cxpWebRuntimeConfig';
        const cachedConfig = sessionStorage.getItem(sessionStorageKey);
        if (cachedConfig) {
            try {
                const parsedConfig: unknown = JSON.parse(cachedConfig);

                if (
                    parsedConfig !== null &&
                    typeof parsedConfig === 'object' &&
                    !Array.isArray(parsedConfig)
                ) {
                    resolve(parsedConfig as RuntimeConfig);
                    return;
                }
            } catch {
                // Cached value is not valid JSON.
            }

            // Remove invalid cached configuration before fetching fresh config.
            sessionStorage.removeItem(sessionStorageKey);
        }

        axios.get(urlWithBasePath('/api/config')).then(
            serverConfig => {
                sessionStorage.setItem(sessionStorageKey, JSON.stringify(serverConfig.data));
                resolve(serverConfig.data);
            },
            error => {
                reject(error);
            }
        );
    });
};

export default getRuntimeConfig;
